import React from 'react';
import { Button, Modal, ModalBody, ConfirmationDialog } from '@panwds/react-ui';
import { twMerge } from '@panwds/tailwindcss';
import { MODAL_BORDER_CLASS } from '../utils/modalLayout';

/**
 * A small confirmation prompt rendered as its own `Modal`, wrapping PANWDS
 * `ConfirmationDialog` for the title/prompt/actions layout.
 *
 * Used for the two destructive-intent confirmations in the subscription flow: discarding
 * unsaved edits and removing a subscribed product. `ConfirmationDialog` is layout-only
 * (it renders a `Title`, a `Body`, and a centered action row), so it must be wrapped in a
 * `Modal` to actually overlay.
 *
 * Safe to render while ManageSubscriptionsModal is open: PANWDS `Modal` portals at a
 * fixed z-index, so the later-mounted dialog paints above it, and `useModalBackdropClose`
 * only reacts to the last portal in the DOM.
 *
 * `minHeight: 0` is load-bearing. `Modal` defaults to `size="md"`, whose preset hard-sets
 * `minHeight: '320px'` on the dialog's root `Card`, and `ConfirmationDialog` centers its
 * content with `h-full` + `m-auto` -- so a two-line prompt floats in the middle of a
 * 320px box with large dead space above and below it. Overriding `minHeight` through our
 * own `style` prop (which `Modal` spreads *after* the size preset) lets the dialog size to
 * its actual content. `0` also deliberately short-circuits `Modal`'s
 * `style.minHeight && ...` clamp, which would otherwise re-wrap the value in a `min()`.
 */
const ConfirmModal = ({
    title,
    prompt,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    onConfirm,
    onCancel,
    dataTestId = 'confirm-modal',
}) => (
    <Modal
        isOpen
        onClose={onCancel}
        dataTestId={dataTestId}
        addClassName={twMerge('ssmutw-bg-[var(--ssmu-page-bg-primary)]', MODAL_BORDER_CLASS)}
        style={{ width: 440, maxWidth: 'calc(100vw - 32px)', minHeight: 0 }}
        eventsToClose={{ enableClickOutside: true, enableEscapeKey: true }}
    >
        <ModalBody>
            {/*
              * `prompt` is passed as a plain string, not wrapped in a `<Body>`:
              * `ConfirmationDialog` already renders the prompt inside its own `<Body>`
              * (a `<p>`), so wrapping it would nest `<p>` inside `<p>` -- invalid DOM
              * that React warns about at runtime.
              */}
            <ConfirmationDialog
                title={title}
                prompt={prompt}
                actions={
                    <>
                        <Button appearance="secondary" onClick={onCancel} dataTestId={`${dataTestId}-cancel`}>
                            {cancelLabel}
                        </Button>
                        <Button appearance="primary" onClick={onConfirm} dataTestId={`${dataTestId}-confirm`}>
                            {confirmLabel}
                        </Button>
                    </>
                }
            />
        </ModalBody>
    </Modal>
);

export default React.memo(ConfirmModal);
